import { useCallback, useMemo } from "react";
import IconsEnum from "../../classes/enums/IconsEnum";
import useTranslation from "../../hooks/i18n/useTranslation";
import { usePatientData } from "../../hooks/queries/usePatientData";
import { useRouteState } from "../../hooks/useRouteState";
import Button from "../button/Button";
import IconButton from "../iconButton/IconButton";
import AppLogos from "../logos/AppLogos";
import UserButton from "../userButton/UserButton";
import "./TopBar.css";

import { ROUTE_TYPES } from "../../classes/constants/routeConstants";
import { useAppContext } from "../../classes/contexts/AppContext";
import { IconSize } from "../../classes/enums/ButtonEnum";
import GenerateReportPopup from "../../modules/report/GenerateReportPopup";
import ButtonWithIcon from "../button/ButtonWithIcon";
import NavigationTab from "../navigationTab/NavigationTab";
import { TooltipOrientation } from "../tooltip/Tooltip";

function TopBar() {
  const { t } = useTranslation();
  const { showPopup } = useAppContext();
  const {
    params,
    isLogin,
    isTwinContext,
    navigateToPatientDashboard,
    navigateToHome,
    navigateToSupport,
    isRouteActive,
    goBack,
    isUtilityRoute: isUtilityRouteActive,
  } = useRouteState();

  const { patientData, loading: patientLoading } = usePatientData(
    params.patientId,
    0
  );

  const showTabs = useCallback(() => {
    return (
      isRouteActive(ROUTE_TYPES.SUPPORT) ||
      isRouteActive(ROUTE_TYPES.SETTINGS) ||
      isRouteActive(ROUTE_TYPES.PATIENT_DASHBOARD) ||
      isRouteActive(ROUTE_TYPES.DIGITAL_TWIN) ||
      isRouteActive(ROUTE_TYPES.CHECK) ||
      isRouteActive(ROUTE_TYPES.ALIGN) ||
      isRouteActive(ROUTE_TYPES.VALIDATION)
    );
  }, [isRouteActive]);

  const showReportButton = useCallback(() => {
    return (
      isTwinContext &&
      (isRouteActive(ROUTE_TYPES.DIGITAL_TWIN) ||
        isRouteActive(ROUTE_TYPES.CHECK) ||
        isRouteActive(ROUTE_TYPES.ALIGN))
    );
  }, [isTwinContext, isRouteActive]);

  const hidePatientSpecificTabs = useCallback(() => {
    return (
      isRouteActive(ROUTE_TYPES.SUPPORT) || isRouteActive(ROUTE_TYPES.SETTINGS)
    );
  }, [isRouteActive]);

  const patientName = useMemo(() => {
    if (patientData && patientData.first_name && patientData.last_name) {
      return `${patientData.first_name[0]}. ${patientData.last_name}`;
    }
    return "";
  }, [patientData]);

  const shouldShowBackButton = useMemo(() => {
    return isUtilityRouteActive;
  }, [isUtilityRouteActive]);

  const shouldShowHomeButton = useMemo(() => {
    return !isUtilityRouteActive;
  }, [isUtilityRouteActive]);

  const handleGenerateReport = useCallback(() => {
    showPopup(<GenerateReportPopup />);
  }, [showPopup]);

  const renderPrimaryNavigationButton = useCallback(() => {
    if (shouldShowBackButton) {
      return (
        <ButtonWithIcon
          key="back-button"
          value={t("289")}
          onClick={goBack}
          icon={IconsEnum.GOBACK}
          className="top-bar-action-button"
        />
      );
    }

    if (shouldShowHomeButton) {
      return (
        <Button
          key="home-button"
          value={t("122")}
          onClick={navigateToHome}
          className="top-bar-action-button"
        />
      );
    }

    return null;
  }, [shouldShowBackButton, shouldShowHomeButton, t, goBack, navigateToHome]);

  return (
    <div className="App-top-bar-container" data-testid="top-bar">
      <div className="App-top-bar-logos" data-testid="top-bar.logos">
        <AppLogos />
      </div>

      <div className="App-top-bar-left-side">
        {showTabs() && (
          <>
            {renderPrimaryNavigationButton()}
            {!hidePatientSpecificTabs() && (
              <Button
                key={1}
                value={patientName}
                onClick={() => navigateToPatientDashboard(params.patientId)}
                disabled={patientLoading}
                isActive={isRouteActive(ROUTE_TYPES.PATIENT_DASHBOARD)}
                className="top-bar-action-button"
              />
            )}
            {!hidePatientSpecificTabs() && <NavigationTab />}
          </>
        )}
      </div>

      {!isLogin && (
        <div className="App-top-bar-right-side">
          <div className="App-top-bar-action-buttons">
            {showReportButton() && (
              <ButtonWithIcon
                // add translation for "Generate Report"
                value={t("236")}
                isLightBlue={false}
                isInTopBar={true}
                onClick={handleGenerateReport}
              />
            )}
            <IconButton
              onClick={navigateToSupport}
              icon={IconsEnum.HELP}
              isCircle={false}
              isLightBlue={false}
              iconSize={IconSize.DEFAULT}
              title={t("432")}
              tooltipOrientation={TooltipOrientation.Bottom}
            />
          </div>
          <UserButton isExpandable={true} />
        </div>
      )}
    </div>
  );
}

export default TopBar;
